// components/industry-templates/PhotographyEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Camera, Award, Users, Image } from 'lucide-react';

export const PhotographyEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Photo Exhibition */}
      {industryData?.exhibitions && (
        <Card className="border-0 bg-gradient-to-br from-purple-500/20 to-blue-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Camera className="w-8 h-8" style={{ color: theme.primary }} />
              Photography Exhibitions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.exhibitions.map((exhibition: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{exhibition.theme}</h3>
                      <p className="text-slate-300">{exhibition.photographer}</p>
                    </div>
                    <Badge style={{ backgroundColor: theme.primary }}>
                      {exhibition.photos_displayed} Photos
                    </Badge>
                  </div>
                  {exhibition.genre && (
                    <div className="flex gap-2 mt-3">
                      {exhibition.genre.map((g: string, i: number) => (
                        <Badge key={i} variant="outline" className="border-white/20 text-white">
                          {g}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photo Walks */}
      {industryData?.photo_walks && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-6 h-6" style={{ color: theme.primary }} />
              Guided Photo Walks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.photo_walks.map((walk: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <h4 className="font-bold text-white mb-2">{walk.location}</h4>
                  <p className="text-sm text-slate-400 mb-2">Guide: {walk.guide}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{walk.time}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">{walk.duration}</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Workshops */}
      {industryData?.workshops && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Image className="w-6 h-6" style={{ color: theme.primary }} />
              Photography Workshops
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.workshops.map((workshop: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg">
                  <h4 className="font-bold text-white mb-1">{workshop.topic}</h4>
                  <p className="text-sm text-slate-400 mb-2">{workshop.instructor}</p>
                  <div className="flex gap-2">
                    <Badge style={{ backgroundColor: theme.primary }}>{workshop.level}</Badge>
                    <Badge variant="outline" className="border-white/20 text-white">
                      {workshop.duration}
                    </Badge>
                    {workshop.equipment_provided && (
                      <Badge variant="outline" className="border-green-500 text-green-400">
                        Equipment Provided
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Photo Contest */}
      {industryData?.photo_contest && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(168, 85, 247, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Photography Contest
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg p-5 text-center">
                <p className="text-3xl font-bold mb-2" style={{ color: theme.primary }}>
                  {industryData.photo_contest.prize_money}
                </p>
                <p className="text-white">Total Prize Money</p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {industryData.photo_contest.categories?.map((category: string, idx: number) => (
                  <div key={idx} className="p-3 bg-white/5 rounded-lg text-center">
                    <p className="text-white font-medium text-sm">{category}</p>
                  </div>
                ))}
              </div>
              {industryData.photo_contest.deadline && (
                <p className="text-center text-slate-400 text-sm">
                  Submission Deadline: {industryData.photo_contest.deadline}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
